<template>
  <main
    class="fill-height rel"
    v-resize="redimension"
  >

    <Error v-if="error"/>

    <Loading v-else-if="loading" />

    <div
      v-else
      class="builder builder-modern layer"
      :class="{
        'show-bike-fit': showBikeFit,
        'builder-initial': !composition.length,
        'show-details-complete': this.detailsComplete,
        'use-drawer': useDrawer,
        'is-drawer-open': useDrawer && drawerOpen,
        'is-drawer-full': useDrawer && drawerOpen && drawerFull,
        'is-comparing': !!compareEntry
      }"
    >

      <Header />
      <div class="builder-tools">
        <button
          v-if="detailsComplete"
          type="button"
          class="builder-back"
          @click="detailsComplete = false"
        >
          <v-icon small color="white">$prev</v-icon>
          Back to bike builder
        </button>
        <Breadcrumbs
          v-else
          class="builder-breadcrumbs"
          :value="index"
          :items="crumbs"
          :ids="steps.map(a => a.id)"
          :selected="selection"
          :disabled="steps.map((_, i) => isDisabled(i))"
          @input="onChangeIndex"
        />

    <SavedBuilds
      v-if="!loading && !error"
      ref="savedBuilds"
      :composition="compositionID"
      :frame-name="composition.length ? composition[0].item.name : 'My bike'"
      :count="composition.length"
      :complete="composition.length > 0 && !hideForm"
      :blocked="detailsComplete || description.show || form || share || special"
      :covered="useDrawer && drawerOpen && !wide && !detailsComplete && !showBikeFit"
      @gate="emailGate = $event"
      :decode="decodeBuild"
      @restore="restoreSavedBuild"
      @new="startNewBuild"
    />
      </div>

      <div class="builder-body">
      <details v-if="showTyreBanner" class="tyre-fit-notice" :class="'is-' + tyreNotice.level">
        <summary>
          <span>{{ tyreNoticeSummary }}</span>
          <span class="tyre-fit-details-label">Details</span>
        </summary>
        <div class="tyre-fit-details">
          {{ tyreNotice.text }}
          <a v-if="tyreNotice.clearance" :href="tyreNotice.clearance.source" target="_blank" rel="noopener noreferrer">Frame specifications</a>
        </div>
      </details>

        <p v-if="composition.length && framePreviewNote" class="allied-preview-note" role="status">{{ framePreviewNote }}</p>

        <div class="builder-content">

          <div v-if="!composition.length" class="builder-start layer pa-10">
            <div v-if="company.introHtml" v-html="company.introHtml"></div>
            <p class="builder-start--info">
              CHOOSE A FRAME TO START BUILDING YOUR BIKE
            </p>
          </div>

          <template v-else>

            <div ref="bikeBox" class="builder-bike" :class="{ 'has-presets': presets.length && index === 0 && !cageEdit && !detailsComplete, 'is-spotlight': spotlight }">
              <Bike
                ref="bike"
                :items="composition"
                interactive
                @buffer="refreshSpotSnap"
                @select-part="onSelectPart"
                :cage-edit-mode="cageEdit"
                :cage-geometry="cageGeometry"
                @update-anchor="onCageAnchor"
                @update-cage-angle="onCageAngle"
                @update-cage-scale="onCageScale"
                @update-cage-flip="onCageFlip"
              />
              <!-- Desktop: the part's finishes under the view / Ride buttons (B's while comparing). -->
              <FinishChips
                v-if="useDrawer && wide && finishColors.length > 1 && !detailsComplete && !showBikeFit && !cageEdit"
                class="bike-finishes"
                dropdown
                :label="finishLabel( step.id )"
                :colors="finishColors"
                :value="compare ? compare.color : selectedColor"
                @input="onDrawerColor"
              />
              <!-- A/B compare: B is drawn over A and revealed right of the wipe. -->
              <div v-if="compareEntry" class="bike-compare" :style="{ clipPath: `inset(0 0 0 ${ wipe }%)` }" aria-hidden="true">
                <Bike ref="bikeB" :items="compareComposition" />
              </div>
              <div
                v-if="compareEntry"
                class="bike-wipe"
                :style="{ left: `${ wipe }%` }"
                role="slider"
                tabindex="0"
                aria-label="Compare slider: drag or use the arrow keys"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round( wipe )"
                @pointerdown.prevent="startWipe"
                @keydown.left.prevent="wipe = Math.max( 0, wipe - 5 )"
                @keydown.right.prevent="wipe = Math.min( 100, wipe + 5 )"
              >
                <span class="bike-wipe--tag is-a">A</span>
                <span class="bike-wipe--knob" aria-hidden="true">‹ ›</span>
                <span class="bike-wipe--tag is-b">B</span>
              </div>
              <PartSpotlight v-if="spotlight" :kind="spotlight" :image="spotSnap" :data="spotData" :from="spotFrom" @close="closeSpotlight" @open="onSelectPart( spotlight )" />
              <button v-if="spotlightAvailable" type="button" class="spot-toggle" :aria-pressed="String( !!spotlight )" @click="spotlightFor = spotlight ? '' : step.id">
                <v-icon small>{{ spotlight ? 'mdi-bike' : 'mdi-magnify-plus-outline' }}</v-icon>{{ spotlight ? 'Show on bike' : ( step.id === 'tyres' ? 'Tyre details' : 'Wheel details' ) }}
              </button>
              <transition name="rim-fade">
                <div v-if="rimCallout" class="rim-callout" role="status">
                  <template v-for="mark in rimCallout.marks">
                    <span :key="mark.side + '-tick'" class="rim-tick" :class="[ 'is-' + mark.side, { 'is-outside': mark.outside }]" :style="mark.tick"></span>
                    <span :key="mark.side + '-label'" class="rim-label" :class="[ 'is-' + mark.side, { 'is-outside': mark.outside }]" :style="mark.label">
                      <small>Rim depth<template v-if="compareEntry"> · {{ mark.side === 'rear' ? 'A' : 'B' }}</template></small>
                      <b>{{ mark.text }}</b>
                    </span>
                  </template>
                </div>
              </transition>
              <!-- Phone layout: per-step controls sit on the preview so the
                   selector below keeps the same height on every step. -->
              <div v-if="current && !showBikeFit && !detailsComplete" class="bike-quick">
                <div class="bike-quick--left">
                  <label v-if="quickCategories.length" class="bike-quick--pill">
                    <span>{{ categoryLabel }}</span>
                    <select :value="categoryFilter" aria-label="Filter models" @change="categoryFilter = $event.target.value">
                      <option v-for="option in quickCategories" :key="option.value" :value="option.value">{{ option.value === 'All' ? 'All types' : option.text }}</option>
                    </select>
                  </label>
                  <label v-if="step.id === 'tyres' && tyreSelection" class="bike-quick--pill">
                    <span>{{ selectedTyreWidth ? `${ selectedTyreWidth } mm` : 'Width' }}</span>
                    <select :value="selectedTyreWidth || ''" aria-label="Tyre width" :disabled="!tyreWidthOptions.length" @change="setTyreWidth( Number( $event.target.value ))">
                      <option value="" disabled>Width</option>
                      <option v-for="option in tyreWidthOptions" :key="option.value" :value="option.value">{{ option.value }} mm</option>
                    </select>
                  </label>
                  <button v-if="step.id === 'framesets' && specialBuildList.length" type="button" class="bike-quick--pill" @click="special = true">Special builds</button>
                </div>
                <div v-if="quickColors.length > 1" class="bike-quick--colors" role="radiogroup" :aria-label="`Colour: ${ (quickColors[ selectedColor ] || {}).colorName || '' }`">
                  <Color
                    v-for="( color, i ) in quickColors"
                    :key="i"
                    tag="button"
                    type="button"
                    role="radio"
                    :aria-checked="String( i === selectedColor )"
                    :aria-label="color.colorName || `Colour ${ i + 1 }`"
                    :title="color.colorName"
                    :value="[ color.color, color.color2 ]"
                    :selected="i === selectedColor"
                    @click="selectedColor = i"
                  />
                </div>
              </div>
              <BuildPresets
                v-if="presets.length && index === 0 && !cageEdit && !detailsComplete"
                :items="presets"
                :value="activePreset"
                @apply="applyPreset"
              />
              <v-snackbar v-model="presetNotice.show" :timeout="6000" color="#344b35">
                {{ presetNotice.text }}
                <template v-slot:action="{ attrs }">
                  <v-btn text v-bind="attrs" @click="undoPreset">Undo</v-btn>
                </template>
              </v-snackbar>
              <div v-if="cageEdit" class="cage-edit-panel">
                <span class="cage-edit-panel--label">
                  CAGE EDIT — drag to move, scroll to rotate, ⇧+scroll to resize{{ cagePending.cage_scale ? ` (×${cagePending.cage_scale})` : '' }}, double-click to mirror
                </span>
                <Btn small color="bb-primary" :disabled="!!cageSaving" :loading="cageSaving === 'model'" @click="saveCagePositions( 'model' )" dark>
                  Save for this model
                </Btn>
                <Btn small color="bb-primary" :disabled="!!cageSaving" :loading="cageSaving === 'colour'" @click="saveCagePositions( 'colour' )" outlined>
                  Save for this colour only
                </Btn>
                <Btn small text :disabled="!!cageSaving" @click="cageEdit = false">Exit</Btn>
              </div>
              <v-snackbar v-model="cageNotice.show" :timeout="cageNotice.error ? -1 : 6000" :color="cageNotice.error ? 'error' : 'success'" multi-line>
                {{ cageNotice.text }}
                <template v-slot:action="{ attrs }">
                  <v-btn text v-bind="attrs" @click="cageNotice.show = false">Close</v-btn>
                </template>
              </v-snackbar>
            </div>

            <BtnSpecialBuild
              v-if="specialBuild && !detailsComplete"
              @click="special = true"
              bottom
            />

          </template>
        </div>

        <Details
          v-if="detailsComplete"
          @return="detailsComplete = false"
          @form="showSave"
          @quote="showForm"
          @download-pdf="downloadBuildPdf"
          :pdf-busy="pdfBusy"
          :pdf-error="pdfError"
          @share="share = true"
          @special-build="special = true"
          @print="initPrint"
          :items="detailsInfo"
          :special-build="specialBuild"
          :price="price"
          :weight="weight"
          :print="print"
        />

        <Footer
          ref="footer"
          v-else
          :price="price"
          :weight="weight"
          :climb-setup="climbSetup"
          :climb-reference="climbReference"
          :special-build="specialBuild"
          @reset="reset"
          @details="showDetails"
          @share="share = true"
          @form="showSave"
        />

      </div>

      <nav
        v-if="!detailsComplete"
        class="builder-nav"
      >

        <div class="builder-nav--top">

          <v-btn icon aria-label="Previous component" :disabled="prevDisabled" @click="prev">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <span class="builder-category-title">
            {{ step.title }}<span class="builder-nav--count">&nbsp;({{ index + 1 }} of {{ steps.length }})</span>
          </span>

          <v-btn icon aria-label="Next component" :disabled="nextDisabled" @click="next">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

        </div>

            <PartDrawer
              v-if="useDrawer && wide && !showBikeFit"
              docked
              :title="step.title"
              :step="step"
              :items="items"
              :value="compare ? compare.selected : selectedItem"
              :color-index="compare ? compare.color : selectedColor"
              :selection="stepSelection"
              :reference="compare ? selectedItem : null"
              :changing="compare ? 'B' : ''"
              :card="compare ? compareSpecs : dockSpecs"
              :categories="quickCategories"
              :category.sync="categoryFilter"
              :width-options="!compare && step.id === 'tyres' && tyreSelection ? tyreWidthOptions : []"
              :width="selectedTyreWidth"
              @input="onDrawerPick"
              @pick="onDrawerPickColour"
              @color="onDrawerColor"
              @width="setTyreWidth"
              @description="onDescribe"
              :expandable="spotlightAvailable"
              @more-info="openSpotlight"
            >
              <template v-if="compareEntry">
                <span class="part-drawer--changing">Choosing <b>B</b></span>
                <button type="button" @click="compare = null">Exit compare</button>
                <button type="button" class="is-go" @click="useCompareB">Use B</button>
              </template>
              <button v-else type="button" :disabled="!canCompare" @click="openCompare">Compare with another</button>
            </PartDrawer>

            <BikeInfo
              v-else-if="current && !current.noCages && !current.noPedals && !detailsComplete"
              ref="info"
              :item="current"
              :color="selectedColor"
              :special-builds="specialBuildList"
              :tyre-width="selectedTyreWidth"
              :width-options="tyreWidthOptions"
              :width-status="tyreWidthStatus"
              :clearance-text="clearanceText"
              @width="setTyreWidth"
              @input="selectedColor = $event"
              @description="showDescription(current)"
              @special-build="special = true"
            />

        <BikeItems
          :key="step.id"
          v-model="selectedItem"
          v-if="!showBikeFit && !( useDrawer && wide )"
          :type="step.title"
          :category.sync="categoryFilter"
          :items="items"
          :selection="stepSelection"
          @description="showDescription($event, false)"
        />

        <BikeFit
          v-else-if="showBikeFit"
          class="builder-nav--bikefit"
        />

        <Btn
          class="builder-nav--continue outline-top light--border"
          color="bb-primary"
          height="70"
          :disabled="showBikeFit ? false : nextDisabled"
          :dark="!nextDisabled"
          @click="showBikeFit ? showForm() : next()"
          text tile block
        >
          {{ showBikeFit ? 'Contact us today' : `Continue to ${ (steps[index + 1] || {}).title || 'overview' }` }}
          <v-icon right v-text="'$next'"/>
        </Btn>

      </nav>

      <!-- Phones: the selected part's card with Change / Compare / Next. -->
      <div v-if="useDrawer && !detailsComplete && !showBikeFit && !drawerOpen" class="part-dock">
        <div v-if="compareEntry" class="part-dock--compare">
          <PartCard :specs="dockSpecs" letter="A" :eyebrow="step.title" compact />
          <PartCard :specs="compareSpecs" letter="B" :eyebrow="step.title" compact />
        </div>
        <PartCard
          v-else-if="dockSpecs"
          :specs="dockSpecs"
          :image="dockImage"
          :eyebrow="step.title"
          :describable="!current || ( !current.noCages && !current.noPedals )"
          :expandable="spotlightAvailable"
          @description="onDescribe( current )"
          @expand="openSpotlight"
        />
        <div class="part-dock--bar">
          <button type="button" class="is-main" @click="drawerOpen = true">
            {{ compareEntry ? 'Change B' : stepSelection ? `Change ${ step.title.toLowerCase() }` : `Choose ${ step.title.toLowerCase() }` }}
          </button>
          <button v-if="compareEntry" type="button" @click="compare = null">Exit</button>
          <button v-else type="button" :disabled="!canCompare" @click="openCompare">Compare</button>
          <button v-if="compareEntry" type="button" class="is-go" @click="useCompareB">Use B</button>
          <button v-else type="button" class="is-go" :disabled="nextDisabled" @click="next">
            Next <v-icon small>mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>

      <PartDrawer
        v-if="useDrawer && drawerOpen && !detailsComplete && !showBikeFit"
        class="part-drawer-sheet"
        :full="drawerFull"
        :title="step.title"
        :step="step"
        :items="items"
        :value="compare ? compare.selected : selectedItem"
        :color-index="compare ? compare.color : selectedColor"
        :selection="stepSelection"
        :reference="compare ? selectedItem : null"
        :changing="compare ? 'B' : ''"
        :card="compare ? compareSpecs : dockSpecs"
        :categories="quickCategories"
        :category.sync="categoryFilter"
        :width-options="!compare && step.id === 'tyres' && tyreSelection ? tyreWidthOptions : []"
        :width="selectedTyreWidth"
        @input="onDrawerPick"
        @pick="onDrawerPickColour"
        @color="onDrawerColor"
        @width="setTyreWidth"
        @description="onDescribe"
        :expandable="spotlightAvailable"
        @more-info="openSpotlight"
        @close="drawerOpen = false"
        @expand="drawerFull = $event"
      />

    </div>





    <!-- DIALOGS -->

    <Description
      v-model="description.show"
      :item="description.item"
      :image="description.image"
      :back-button="description.backButton"
      @back="showDetails"
    />


    <Form
      v-model="form"
      :items="composition"
      :tyre-notice="tyreNotice && tyreNotice.text"
    />

    <Share
      v-model="share"
      :id="compositionID"
      :story-busy="storyBusy"
      :story-ready="!!storyFile"
      :story-error="storyError"
      @story="shareStory"
    />

    <SpecialBuilds
      v-model="special"
      :items="specialBuildList"
      :selected="specialBuild"
      @select="getComposition($event.query)"
    />

  </main>
</template>

<script>

  import axios from 'axios';
  import { appBase } from '@/utils/basePath';
  import { mapState } from 'vuex';
  import { CONSTANTS, itemImage, categoryOptions } from '@/utils';

  // COMPONENTS

  import Loading from '../Loading';
  import Error from '../Error';
  import Header from './Header';
  import Footer from './Footer';

  import Breadcrumbs from './Breadcrumbs';
  import BikeItems from './BikeItems';
  import BikeInfo from './BikeInfo';
  import BikeFit from './BikeFit';
  import Bike from './Bike';
  import PartCard from './PartCard';
  import PartDrawer from './PartDrawer';
  import PartSpotlight from './PartSpotlight';
  import FinishChips from './FinishChips';

  // DIALOGS
  import SpecialBuilds from './SpecialBuilds';
  import Description from './Description';
  import Details from './Details';
  import Share from './Share';
  import Form from './Form';
  import SavedBuilds from './SavedBuilds';
  import BuildPresets from './BuildPresets';
  import { frameClearance, tyreFit, tyreWidths, validTyreWidth } from '@/utils/tyreFit';
  import { alliedGroupsetFits } from '@/utils/alliedFit';
  import { buildWeight, partWeight, kg } from '@/utils/weight';
  import { SEPARATOR, decodeComposition, entryPrice } from '@/utils/composition';
  import { bmcCockpitName, bmcBarsForFrame, preselectBmcCockpit } from '@/utils/bmcCockpit';
  import { preselectSeatpost } from '@/utils/defaultSeatpost';
  import { isSekaRdc, sekaBarsForFrame } from '@/utils/sekaCockpit';
  import { extremaCockpitName, extremaBarsForFrame, preselectExtremaCockpit, matchExtremaFinish } from '@/utils/threeTCockpit';
  import { isGravelFrame, pickPart, presetsFor } from '@/utils/presets';
  import { avonaPartFits, avonaPartsForFrame, preselectAvonaParts } from '@/utils/avonaParts';
  import { partSpecs, rowImage, rimDepthMm, wheelFigures, tyreFigures, treadImage, finishLabel } from '@/utils/partSpecs';


  // UTILS
  import { Btn, BtnSpecialBuild, Color } from '@/components';

  // Constants

  const ITEMS_LIST = Object.keys( CONSTANTS ).map( key => ({
      id: key,
      ...CONSTANTS[key]
    }))
    .filter( a => a.title );

  export default {
    components: {
      Loading,
      Error,
      Header,
      Footer,
      BtnSpecialBuild,
      SpecialBuilds,
      Breadcrumbs,
      Description,
      Details,
      Share,
      Form,
      SavedBuilds,
      BuildPresets,
      BikeItems,
      BikeFit,
      BikeInfo,
      Bike,
      PartCard,
      PartDrawer,
      PartSpotlight,
      FinishChips,
      Btn,
      Color
    },
    mounted() {
      this.fetch();
      window.addEventListener( 'beforeprint', this.onPrint );
      window.addEventListener( 'afterprint', this.onAfterPrint );
      window.addEventListener( 'keydown', this.onKeydown );
    },
    beforeDestroy() {
      window.removeEventListener( 'beforeprint', this.onPrint );
      window.removeEventListener( 'afterprint', this.onAfterPrint );
      window.removeEventListener( 'keydown', this.onKeydown );
    },
    data() {
      return {
        index: 0,
        emailGate: true,
        error: false,
        print: false,
        selectedItem: null,
        selectedColor: 0,
        categoryFilter: 'All',
        // The part drawer (phones: bottom sheet; desktop: docked in the side column).
        useDrawer: true,
        // Above the phone breakpoint the drawer sits open in the side column.
        wide: window.innerWidth > 700,
        drawerOpen: false,
        drawerFull: false,
        // A/B compare of the current step: B is { selected, color } in this step's items.
        compare: null,
        wipe: 50,
        rimCallout: null,
        // The step whose part details (spotlight) were asked for: by clicking the
        // wheel or tyre on the bike, the card's More info, or Wheel / Tyre details.
        spotlightFor: '',
        // The front wheel under its tyre, drawn by the bike (see Bike partSnapshot).
        spotSnap: '',
        // Where the front wheel sat on the bike when the spotlight opened (fly-out start).
        spotFrom: null,
        selection: [],
        noCages: { id: 0, name: 'No bottle cages', type: 'cages', noCages: true,
          colors: [{ price: 0, color: { price: 0, colorName: 'None' } }] },
        noPedals: { id: 0, name: 'No pedals', type: 'pedals', noPedals: true,
          colors: [{ price: 0, color: { price: 0, colorName: 'None' } }] },
        form: false,
        share: false,
        special: false,
        detailsComplete: false,
        contentMargin: 0,
        footerHeight: 0,
        pdfBusy: false,
        pdfError: '',
        climbReference: null,
        storyBusy: false,
        storyError: '',
        storyFile: null,
        cageEdit: false,
        cageSaving: false,
        cagePending: {},
        cageNotice: { show: false, error: false, text: '' },
        presetNotice: { show: false, text: '', undo: null },
        presetApplied: { id: null, query: null },
        description: {
          show: false,
          item: null,
          image: null,
          backButton: false
        }
      }
    },
    watch: {
      // Each step starts unfiltered, as when the card list owned the filter.
      'step.id'( id ) {
        this.categoryFilter = 'All';
        this.compare = null;
        // Details stay open only for the step they were asked for.
        if ( this.spotlightFor !== id ) this.spotlightFor = '';
      },
      spotlight( on, was ) {
        if ( on ) this.$nextTick( this.refreshSpotSnap );
        // Opening (not switching parts): the picture lifts off the bike's front wheel.
        if ( on && !was ) this.$nextTick( this.markSpotFrom );
      },
      // A different wheel or tyre: the old picture would be wrong until redrawn.
      spotKey() {
        this.spotSnap = '';
      },
      // The preview row changes height with the drawer and compare cards.
      drawerOpen( open ) { if ( !open ) this.drawerFull = false; this.resizeBikes(); },
      rimKey: 'showRimDepth',
      compare( value, old ) { if ( !value !== !old ) this.resizeBikes(); },
      selectedItem( value ) {
        const { selection, index, step } = this;
        if ( value != null ) {
          // Restoring the active step is navigation, not a build change.
          // Keep the selection identity so the canvas does not reload its layers.
          const saved = selection[index];
          if ( saved && saved.props.id === step.id &&
            saved.selected === value && saved.item === this.current ) return;

          var replace = 0;
          var color = 0;

          if ( this.current && selection[index] ) {
            if ( selection[index].item && selection[index].item.type === this.current.type ) {
              replace = 1;
            }
            color = Math.min( this.current.colors.length - 1, selection[index].color );
          }

          this.selectedColor = color;
          selection.splice( index, replace, {
            props: step,
            selected: value,
            item: this.current,
            color
          });
          if (step.id === 'framesets') {
            const steps = this.getSteps(this.current), bars = this.bars || [];
            this.selection = preselectExtremaCockpit(preselectBmcCockpit(selection, this.current, bars, steps), this.current, bars, steps, color);
            this.selection = preselectAvonaParts(this.selection, this.current, this.stepItems);
          }
          // Choosing the step before the seatpost fills in a default post.
          const frame = this.selection[0] && this.selection[0].item;
          if (frame) this.selection = preselectSeatpost(this.selection, this.stepItems('seatposts'), this.getSteps(frame));
        }
      },
      selectedColor( color ) {
        const { selection, index } = this;
        const previous = selection[index] && selection[index].color;
        if ( selection[index] ) selection[index].color = color;
        const selected = selection[index];
        if (selected && selected.props.id === 'framesets' && !isSekaRdc(selected.item, color)) {
          const barIndex = selection.findIndex(entry => entry.props.id === 'bars' &&
            /^SEKA Gladius(?: ALTA)?$/.test(entry.item.name || ''));
          if (barIndex !== -1) selection.splice(barIndex, 1);
        }
        if (selected && selected.props.id === 'framesets') {
          const matched = matchExtremaFinish(selection, selected.item, this.bars || [], this.getSteps(selected.item), color);
          if (matched !== selection) this.selection = matched;
        }
        if (previous !== color && selected && selected.props.id === 'tyres' && selected.tyreWidth &&
          !tyreWidths(selected.item, color).includes(selected.tyreWidth)) this.$set(selected, 'tyreWidth', null);
      },
      index( index ) {
        // Preview drawer: arriving at a step with nothing chosen opens the picker.
        if ( this.useDrawer && !this.wide ) this.$nextTick(() => {
          if ( !this.stepSelection && !this.showBikeFit && !this.detailsComplete ) this.drawerOpen = true;
        });
        const { selection } = this;
        if ( selection[index] ) {
          // Saved builds and frame changes can have a different display order.
          const selected = this.items.findIndex( item => item.id === selection[index].item.id );
          selection[index].selected = selected < 0 ? null : selected;
          this.selectedItem  = selection[index].selected;
          this.selectedColor = selection[index].color || 0;
        } else {
          this.selectedItem  = null;
          this.selectedColor = 0;
        }
      },
      selectedFrame(frame) {
        const frameEntry = this.selection.find(entry => entry.props.id === 'framesets');
        const rdc = isSekaRdc(frame, frameEntry ? frameEntry.color : 0);
        const compatible = this.selection.filter(entry =>
          (entry.props.id !== 'groupsets' || alliedGroupsetFits(frame, entry.item)) &&
          avonaPartFits(frame, entry) &&
          (entry.props.id !== 'bars' || rdc || !/^SEKA Gladius(?: ALTA)?$/.test(entry.item.name || '')) &&
          (entry.props.id !== 'bars' || extremaBarsForFrame([entry.item], frame, frameEntry ? frameEntry.color : 0).length));
        // Vue 2 re-runs object-valued watchers when their dependencies change,
        // even when the computed frame is the same object. Preserve the array
        // when nothing was removed so this watcher cannot trigger itself.
        if (compatible.length !== this.selection.length) this.selection = compatible;
      },
      compositionID( id ) {
        this.setRoute( id );
        // A story image is only reused for the build it was rendered from.
        this.storyFile = null;
        this.storyError = '';
      },
      // Swapping a part keeps the build before it as the climb reference.
      // Adding or removing a part changes what is compared, so it clears it.
      climbSetup( value, old ) {
        if ( !value || !old || value.parts !== old.parts ) {
          this.climbReference = null;
        } else if ( value.bikeKg !== old.bikeKg || value.tyreWidth !== old.tyreWidth ) {
          // Name the step only when its own part changed (not a restored build).
          const label = value.stepPart !== old.stepPart && value.stepPart.split( ',' )[0] === old.stepPart.split( ',' )[0]
            ? this.step.title : null;
          this.climbReference = { ...old, label };
        }
      },
      current() {
        this.$nextTick( this.redimension );
      },
      loading() {
        this.$nextTick( this.redimension );
      },
      contentStyle: 'resizeBike',
      steps( steps, old ) {
        if ( steps.length === old.length ) return;
        const selection = this.selection
          .filter( a => steps.find( s => s.id === a.props.id ));

        // When the bars step (re)appears — switching from a frameset with an
        // integrated cockpit to one without — default to the first sorted bar
        // so the build never renders without a handlebar.
        const barStepIdx = steps.findIndex( s => s.id === 'bars' );
        if (
          barStepIdx !== -1 &&
          ! selection.find( a => a.props.id === 'bars' ) &&
          selection.find( a => a.props.id === 'framesets' )
        ) {
          const bars = this.stepItems( 'bars' );
          if ( bars.length ) {
            const step = steps[ barStepIdx ];
            const item = Object.assign( bars[0], { type: 'bars', step });
            selection.splice( Math.min( barStepIdx, selection.length ), 0, {
              props: step,
              selected: 0,
              item,
              color: 0
            });
          }
        }

        this.selection = selection;
      }
    },
    computed: {
      ...mapState([
        'loading',
        'specialBuilds',
        'framesets',
        'bars',
        'groupsets',
        'wheels',
        'tyres',
        'seatposts',
        'saddles',
        'cages',
        'pedals',
        'cageGeometry',
        'company'
      ]),
      steps() {
        const { selection } = this;
        const steps = this.getSteps( selection[0] ? selection[0].item : null );
        return steps.concat([{ title: 'Bike Fit' }]);
      },
      step() {
        return this.steps[ this.index ] || { title: '?' };
      },
      crumbs() {
        return this.steps.map( a => a.title );
      },
      items() {
        return this.stepItems( this.step.id );
      },
      presets() {
        return presetsFor( this.selectedFrame );
      },
      // A chip stays highlighted until any part of the build changes.
      activePreset() {
        return this.presetApplied.query === this.compositionID ? this.presetApplied.id : null;
      },
      quickCategories() {
        return categoryOptions( this.step.title );
      },
      categoryLabel() {
        const option = this.quickCategories.find( o => o.value === this.categoryFilter );
        return !option || option.value === 'All' ? 'All types' : option.text;
      },
      // Same order as BikeInfo, whose index selectedColor refers to.
      quickColors() {
        const item = this.current;
        if ( !item || item.noCages || item.noPedals ) return [];
        return ( item.colors || [] ).map( a => a.color ).sort(( a, b ) => a.priority - b.priority );
      },
      partLimit() {
        const limit = frameClearance( this.selectedFrame, this.selectedGroupset );
        return { tyreLimit: limit && limit.maxMm };
      },
      dockSpecs() {
        return partSpecs( this.stepSelection, this.partLimit );
      },
      dockImage() {
        const entry = this.stepSelection;
        return entry ? rowImage( this.step.title, entry.item, entry.color ) : '';
      },
      // Comparing frames would also need their cockpit and fit rules re-run.
      canCompare() {
        return !!this.stepSelection && this.step.id !== 'framesets' && this.items.length > 1;
      },
      compareEntry() {
        const { compare, step } = this;
        const item = compare && this.items[ compare.selected ];
        if ( !item ) return null;
        const width = this.stepSelection && this.stepSelection.tyreWidth;
        return this.selectionEntry( step, item, compare.selected, compare.color, step.id === 'tyres' ? width : undefined );
      },
      compareComposition() {
        const entry = this.compareEntry;
        return entry ? this.composition.map( a => a.props.id === entry.props.id ? entry : a ) : [];
      },
      // Wheels and tyres steps: the chosen part pulled out of the bike with its
      // figures (desktop while the step is open, phones while the drawer is open).
      // The part being chosen: the build's, or B while comparing.
      finishColors() {
        const item = this.compare ? this.items[ this.compare.selected ] : this.current;
        if ( !item || item.noCages || item.noPedals ) return [];
        return ( item.colors || [] ).map( a => a.color ).sort(( a, b ) => a.priority - b.priority );
      },
      spotlightAvailable() {
        return this.useDrawer && [ 'wheels', 'tyres' ].includes( this.step.id ) && !!this.stepSelection && !this.compareEntry &&
          !this.detailsComplete;
      },
      // Which spotlight is showing: 'wheels', 'tyres' or '' (none).
      spotlight() {
        return this.spotlightAvailable && this.spotlightFor === this.step.id ? this.step.id : '';
      },
      spotData() {
        if ( this.spotlight === 'tyres' ) {
          const entry = this.tyreSelection;
          const f = tyreFigures( entry, this.tyreWidthOptions.map( o => o.value ));
          return {
            name: entry.item.name,
            lead: f.weight && { label: 'Weight, pair', value: f.weight },
            side: [ f.type && { label: 'Type', value: f.type } ].filter( Boolean ),
            top: f.width && { label: f.widthLabel, value: f.width },
            bottom: f.price && { label: 'Price, pair', value: f.price },
            photo: treadImage( entry.item, ( entry.item.colors[ entry.color ] || {} ).color && entry.item.colors[ entry.color ].color.colorName )
          };
        }
        const entry = this.wheelsEntry;
        if ( !entry ) return null;
        const f = wheelFigures( entry );
        return {
          name: entry.item.name,
          lead: f.weight && { label: 'Weight, pair', value: f.weight },
          side: [ f.inner && { label: 'Inner width', value: f.inner } ].filter( Boolean ),
          top: f.depth && { label: 'Rim depth', value: f.depth },
          bottom: f.price && { label: 'Price', value: f.price },
          photo: ''
        };
      },
      wheelsEntry() {
        return this.selection.find( a => a.props.id === 'wheels' && a.item ) || null;
      },
      spotKey() {
        const w = this.wheelsEntry, t = this.tyreSelection;
        return [ this.spotlight, w && w.item.id, w && w.color, t && t.item.id, t && t.color, t && t.tyreWidth ].join();
      },
      // Changes whenever a different wheel (A or B) is on screen in the wheels step.
      rimKey() {
        const a = this.stepSelection, b = this.compareEntry;
        if ( !this.useDrawer || this.step.id !== 'wheels' || !a || this.spotlight ) return '';
        return [ a.item.id, b ? b.item.id : '', this.drawerOpen ].join();
      },
      compareSpecs() {
        return partSpecs( this.compareEntry, this.partLimit );
      },
      stepSelection() {
        const entry = this.selection[ this.index ];
        return entry && entry.item && entry.props.id === this.step.id ? entry : null;
      },
      tyreSelection() {
        return this.selection.find(a => a.props.id === 'tyres' && a.item);
      },
      selectedTyreWidth() { return this.tyreSelection && this.tyreSelection.tyreWidth || null; },
      selectedFrame() { return (this.selection.find(a => a.props.id === 'framesets') || {}).item; },
      framePreviewNote() {
        const entry = this.selection.find(a => a.props.id === 'framesets');
        const color = entry && entry.item && entry.item.colors[entry.color];
        return color && (color.previewNote || color.color && color.color.previewNote) || '';
      },
      selectedGroupset() { return (this.selection.find(a => a.props.id === 'groupsets') || {}).item; },
      clearanceText() {
        const limit = frameClearance(this.selectedFrame, this.selectedGroupset);
        if (!limit || !limit.maxMm) return 'Tyre clearance: needs confirmation';
        return `Published tyre limit: ${limit.maxMm} mm${limit.review ? ' · setup needs confirmation' : ''}`;
      },
      tyreWidthOptions() {
        const selected = this.tyreSelection;
        const limit = frameClearance(this.selectedFrame, this.selectedGroupset);
        return selected ? tyreWidths(selected.item, selected.color).map(width => ({
          text: `${width} mm${limit && limit.maxMm && width > limit.maxMm ? (limit.review ? ' · confirm frame clearance' : ' · exceeds frame limit') : ''}`, value: width
        })) : [];
      },
      tyreWidthStatus() {
        const selected = this.tyreSelection;
        const color = selected && selected.item.colors[selected.color];
        if (!color || color.tyreSizeStatus !== 'verified') return 'Width availability is unverified for this tyre and colour. Ask our team.';
        const checked = color.tyreSizesCheckedAt ? new Date(color.tyreSizesCheckedAt).toLocaleString() : '';
        return this.tyreWidthOptions.length ? `In stock for a pair at Barcelona / Girona. Stock updated ${checked}.`
          : 'No 700c widths with confirmed stock for a pair at Barcelona / Girona.';
      },
      tyreNoticeSummary() {
        const notice = this.tyreNotice;
        if (!notice) return '';
        if (notice.level === 'error') return notice.text.split(' Choose ')[0];
        if (!this.selectedTyreWidth) return this.tyreWidthOptions.length
          ? 'Choose a tyre width to check clearance.' : 'Tyre width unavailable · clearance not checked.';
        if (notice.level === 'info') return `${this.selectedTyreWidth} mm tyre · ${notice.clearance.maxMm} mm frame limit`;
        return `${this.selectedTyreWidth} mm tyre · availability or clearance needs confirmation.`;
      },
      showTyreBanner() {
        // Only take vertical space when there is a real problem: no prompt before a width is chosen, no "fits" line.
        const notice = this.tyreNotice;
        if (!notice || notice.level === 'info') return false;
        return !!this.selectedTyreWidth || !this.tyreWidthOptions.length;
      },
      tyreNotice() {
        if (!this.tyreSelection) return null;
        const result = tyreFit(this.selectedFrame, this.selectedGroupset, this.selectedTyreWidth);
        if (!this.selectedTyreWidth && !this.tyreWidthOptions.length) return { ...result, text: this.tyreWidthStatus + ' Frame clearance cannot be checked until a width is chosen.' };
        if (this.selectedTyreWidth && !this.tyreWidthOptions.some(o => o.value === this.selectedTyreWidth)) {
          return { ...result, level: result.level === 'error' ? 'error' : 'warning',
            text: `${result.text} The saved width is not currently verified as available; choose an available size or ask our team.` };
        }
        return result;
      },
      current() {
        const item = this.items[ this.selectedItem ];
        if ( item ) {
          item.type = this.step.id;
          item.step = this.step;
        }
        return item;
      },
      prevDisabled() {
        return this.isDisabled( this.index - 1 );
      },
      nextDisabled() {
        return this.isDisabled( this.index + 1 );
      },
      composition() {
        const steps = this.steps.map( a => a.id );
        const { selection } = this;
        return selection.filter( a => a.item && steps.indexOf( a.item.type ) !== -1 );
      },
      hideForm() {
        return this.composition.length < this.steps.length - 1;
      },
      showBikeFit() {
        return this.index === this.steps.length - 1;
      },
      compositionID() {
        return this.getQuery( true );
      },
      detailsInfo() {
        return this.composition.map(( a, item ) => {
          item = a.item.colors[ a.color ];
          const type = a.item.step.title;
          const price = entryPrice( a );
          // Tyres and bottle cages are a PAIR; the stored price is the pair total.
          const pair = !a.item.noCages && [ 'Tyres', 'Bottle Cages' ].includes( type );
          return {
            type,
            name: a.item.name + (a.props.id === 'tyres' && a.tyreWidth ? ` · ${a.tyreWidth} mm` : ''),
            item: a.item,
            price,
            pair,
            unitPrice: pair ? +( price / 2 ).toFixed( 2 ) : price,
            colorName: item.color.colorName,
            color: [ item.color.color, item.color.color2 ],
            image: itemImage( a.item, a.color ),
            url: item.url,
            weight: partWeight( a )
          }
        });
      },
      price() {
        return this.detailsInfo
          .reduce(( sum, a ) => sum + a.price, 0 );
      },
      weight() {
        return buildWeight( this.composition );
      },
      // What the climb estimate depends on; parts lists the filled steps so a
      // swap can be told apart from adding a part to an empty step.
      climbSetup() {
        if ( !this.weight ) return null;
        const entry = this.composition.find( a => a.props.id === this.step.id );
        return {
          bikeKg: this.weight.grams / 1000,
          tyreWidth: this.selectedTyreWidth,
          parts: this.composition.map( a => a.props.id ).join(),
          stepPart: entry ? [ entry.props.id, entry.item.id, entry.color, entry.tyreWidth ].join() : ''
        };
      },
      specialBuildList() {
        const frameset = ( this.composition[0] || {} ).item;
        if ( ! frameset ) return [];
        return this.specialBuilds.filter( s => s.frameset === frameset.id );
      },
      specialBuild() {
        const query = this.getQuery();
        return this.specialBuildList.find( s => s.query === query );
      },
      contentStyle() {

        const minHeight = window && window.innerHeight > 480 ? '40vh' : '80vh';
        const calculatedHeigth = !this.detailsComplete
          ? this.contentMargin + this.footerHeight
          : 0;

        return {
          minHeight,
          height: calculatedHeigth > 0 ? `calc( 100% - ${ calculatedHeigth }px )` : null,
          marginBottom: `${this.contentMargin}px`
        };
      }
    },
    methods: {
      // The options a step lists, in display order, with every frame
      // compatibility filter applied (also what the build presets pick from).
      stepItems( id ) {
        const items = avonaPartsForFrame( this[ id ] || [], this.selectedFrame, id );
        if ( id === 'cages' ) return [this.noCages, ...items];
        if ( id === 'pedals' ) return [this.noPedals, ...this.sortPedals( items )];
        if ( id === 'groupsets' ) return items.filter(item => alliedGroupsetFits(this.selectedFrame, item));
        if ( id === 'bars' ) return this.sortBars( items );
        if ( id === 'wheels' ) return this.sortWheels( items );
        return items;
      },
      // One positional selection entry, shaped as choosing a card (selectedItem)
      // or opening a shared build (getComposition) creates it.
      selectionEntry( step, item, selected, color = 0, tyreWidth ) {
        const entry = { props: step, selected, item: Object.assign( item, { type: step.id, step }), color };
        if ( step.id === 'tyres' ) entry.tyreWidth = tyreWidth == null ? null : validTyreWidth( tyreWidth );
        return entry;
      },
      // Fills every step after the frameset from that step's own option list.
      applyPreset( preset ) {
        const frameEntry = this.selection[0];
        if ( ! frameEntry || frameEntry.props.id !== 'framesets' ) return;
        const frame = frameEntry.item;
        const steps = this.getSteps( frame );
        const keep = bmcCockpitName( frame ) || extremaCockpitName( frame, frameEntry.color ) ||
          ( isSekaRdc( frame, frameEntry.color ) ? 'SEKA Gladius' : null );
        // Replaced entries are dropped, never mutated, so the old array restores as is.
        const undo = { selection: this.selection.slice(), index: this.index, applied: this.presetApplied };
        const selection = [ frameEntry ];
        let groupset = null;
        for ( const step of steps.slice( 1 )) {
          const options = this.stepItems( step.id );
          const choice = pickPart( preset.id, step.id, options, {
            terrain: isGravelFrame( frame ) ? 'gravel' : 'road',
            keep: step.id === 'bars' ? keep : null,
            fit: width => tyreFit( frame, groupset, width )
          });
          if ( ! choice ) break;
          const item = options[ choice.index ];
          selection.push( this.selectionEntry( step, item, choice.index, choice.color, choice.tyreWidth ));
          if ( step.id === 'groupsets' ) groupset = item;
        }
        this.selection = selection;
        this.syncStep();
        this.presetApplied = { id: preset.id, query: this.compositionID };
        // Close first so the snackbar restarts its timeout on every apply.
        this.presetNotice = { show: false, text: `${ preset.label } applied — tweak any step`, undo };
        this.$nextTick(() => { this.presetNotice.show = true; });
      },
      undoPreset() {
        const { undo } = this.presetNotice;
        this.presetNotice = { show: false, text: '', undo: null };
        if ( ! undo ) return;
        this.selection = undo.selection;
        this.presetApplied = undo.applied;
        if ( this.index === undo.index ) this.syncStep();
        else this.index = undo.index;
      },
      // Point the open step's card and colour at the (replaced) selection.
      syncStep() {
        const entry = this.selection[ this.index ];
        this.selectedItem = entry ? entry.selected : null;
        this.selectedColor = entry ? entry.color || 0 : 0;
        this.$nextTick( this.redimension );
      },
      setTyreWidth(value) {
        const width = validTyreWidth(value);
        if (this.tyreSelection && this.tyreWidthOptions.some(o => o.value === width)) {
          this.$set(this.tyreSelection, 'tyreWidth', width);
          this.$nextTick(this.redimension);
        }
      },
      onKeydown( e ) {
        if (this.emailGate || this.cageSaving) return;
        // Super-user cage editor: Alt+Shift+C
        if ( e.altKey && e.shiftKey && e.code === 'KeyC' ) {
          e.preventDefault();
          this.cagePending = {};
          this.cageEdit = ! this.cageEdit;
        }
      },
      onCageAnchor({ anchor, x, y }) {
        if ( ! this.cageEdit ) return;
        if ( anchor === 'cagesSeat' ) {
          this.cagePending.cage_seat_x = Math.round( x );
          this.cagePending.cage_seat_y = Math.round( y );
        } else if ( anchor === 'cagesDown' ) {
          this.cagePending.cage_down_x = Math.round( x );
          this.cagePending.cage_down_y = Math.round( y );
        }
      },
      onCageAngle({ anchor, angle }) {
        if ( ! this.cageEdit ) return;
        if ( anchor === 'cagesSeat' ) this.cagePending.cage_seat_angle = angle;
        else if ( anchor === 'cagesDown' ) this.cagePending.cage_down_angle = angle;
      },
      onCageScale({ scale }) {
        if ( ! this.cageEdit ) return;
        this.$set( this.cagePending, 'cage_scale', scale );
      },
      onCageFlip({ anchor, flip }) {
        if ( ! this.cageEdit ) return;
        const field = anchor === 'cagesSeat' ? 'cage_seat_flip' : 'cage_down_flip';
        this.$set( this.cagePending, field, flip );
      },
      // scope: 'model'  -> the cage setup belongs to the frameset, and this
      //                    colour goes back to inheriting it.
      //        'colour' -> a tweak for this colour only, leaving the model alone.
      saveCagePositions( scope ) {
        if ( this.cageSaving ) return;
        this.cageNotice.show = false;
        const comp = this.composition[0] || {};
        const frameset = comp.item;
        // Save what is ON SCREEN, not the accumulated edit deltas: the canvas
        // keeps live cage positions across editor toggles while the pending
        // deltas reset, so delta-based saves could silently save nothing.
        const state = this.$refs.bike && this.$refs.bike.getCageState();
        if ( ! frameset || ! state ) {
          this.cageNotice = { show: true, error: true, text: 'Select a frame and bottle cages, then try saving again.' };
          return;
        }
        // Geometry is stored per (frameset, cage): cage models differ in size and
        // plate position, so saving one must not move the others.
        const cageComp = this.composition.find( a => a.item && a.item.type === 'cages' );
        if ( ! cageComp ) {
          console.warn( 'Cage save: no cage selected' );
          this.cageNotice = { show: true, error: true, text: 'Select bottle cages before saving their positions.' };
          return;
        }

        const colorRow = frameset.colors && frameset.colors[ comp.color ] && frameset.colors[ comp.color ].color;
        const payload = {
          idFrameset: frameset.id,
          idCage: cageComp.item.id,
          idFramesetColor: colorRow && colorRow.id,
          scope,
          state
        };

        this.cageSaving = scope;
        console.log( 'Cage save →', payload );

        return axios.post( '/api/admin/cage-geometry', payload, { timeout: 20000 } )
          .then( res => {
            if ( ! res.data || res.data.success !== true || ! Array.isArray( res.data.rows ) || ! res.data.rows.length ) {
              throw new Error( 'The server did not confirm the save.' );
            }
            console.log( 'Cage save ✓ stored', res.data.rows );
            // Mirror into the in-memory geometry so the canvas (which rebuilds on
            // colour change / step navigation) doesn't snap back to stale values
            // until a full reload.
            const rows = this.cageGeometry
              .filter( g => ! ( g.idFrameset === payload.idFrameset && g.idCage === payload.idCage ))
              .concat( res.data.rows.map( r => ({
                ...r,
                idFramesetColor: r.idFramesetColor == null ? null : r.idFramesetColor
              })));
            this.$store.commit( 'set', { cageGeometry: rows });
            this.cagePending = {};
            this.cageEdit = false;
            this.cageNotice = { show: true, error: false, text: scope === 'colour' ? 'Cage positions saved for this colour.' : 'Cage positions saved for this model.' };
          })
          .catch( err => {
            console.error( 'Cage save failed', err.response ? err.response.data : err );
            const shopRoute = window.location.hostname === 'www.velodrom.cc';
            this.cageNotice = {
              show: true,
              error: true,
              text: shopRoute
                ? 'Cage positions were not saved. The shop site does not support saving cage edits. Open bike-builder.onrender.com/bikebuilder/ to edit and save them.'
                : 'Cage positions could not be confirmed as saved. Your edits are still here; please try saving again.'
            };
          })
          .finally(() => this.cageSaving = false );
      },
      fetch() {
        this.$store
          .dispatch( 'getData' )
          .then( res => {
            if ( res.data.error ) {

              this.error = true;
              console.error( res.data );

            } else {

              // console.log( res.data.object );
              this.error = false;
              this.$store.commit( 'set', {
                loading: false,
                ...res.data.object
              });

              this.getComposition();

            }
          })
          .catch( err => {
            this.error = true;
            console.error( err );
          });
      },
      isDisabled( index ) {
        if ( index < 0 ) return true;
        if ( index >= this.steps.length ) return true;
        if ( this.selection.some(s => s.item && s.props.id === this.steps[index].id) ) return false;
        if ( index !== 0 ) {

          let step = this.steps[ index + 1 ];
          let comp = step ? this.selection.find( a => a.props.id === step.id ) : null;
          if ( comp && comp.item ) return false;

          step = this.steps[ index - 1 ];
          comp = step ? this.selection.find( a => a.props.id === step.id ) : null;
          if ( ! comp || ! comp.item ) return true;
        }
        return false;
      },
      finishLabel,
      closeSpotlight() {
        this.spotlightFor = '';
      },
      openSpotlight() {
        this.spotlightFor = this.step.id;
      },
      markSpotFrom() {
        const bike = this.$refs.bike, box = this.$refs.bikeBox;
        const wheel = bike && box && bike.wheelMarks().find( w => w.side === 'Right' );
        if ( !wheel ) return;
        const br = box.getBoundingClientRect(), cr = bike.$el.getBoundingClientRect();
        // The tyre stands a little proud of the wheel layer.
        this.spotFrom = { x: cr.left - br.left + wheel.x, y: cr.top - br.top + wheel.y, size: wheel.r * 2.12 };
        clearTimeout( this.spotFromTimer );
        this.spotFromTimer = setTimeout(() => { this.spotFrom = null; }, 2500 );
      },
      refreshSpotSnap() {
        const bike = this.$refs.bike;
        if ( !this.spotlight || !bike ) return;
        // Wheels under their tyre (as on the bike); tyres as the ring alone.
        const snap = bike.partSnapshot( this.spotlight === 'tyres' ? [ 'tyresRight' ] : [ 'wheelsRight', 'tyresRight' ]);
        if ( snap ) this.spotSnap = snap;
      },
      openCompare() {
        if ( !this.canCompare ) return;
        const a = this.selectedItem;
        // Start B on the next part in the list, so the wipe shows a difference at once.
        const b = this.items.findIndex(( item, i ) => i > a && !item.noCages && !item.noPedals );
        this.compare = { selected: b < 0 ? ( a === 0 ? 1 : 0 ) : b, color: 0 };
        this.wipe = 50;
      },
      useCompareB() {
        const { selected, color } = this.compare;
        this.compare = null;
        this.selectedItem = selected;
        // The selectedItem watcher carries the old finish over; B's finish wins after it.
        this.$nextTick(() => { this.selectedColor = color; });
      },
      onDrawerPick( index ) {
        if ( this.compare ) this.compare = { selected: index, color: 0 };
        else this.selectedItem = index;
      },
      // A swatch on a picture card chooses that part in that finish.
      onDrawerPickColour({ index, color }) {
        if ( this.compare ) { this.compare = { selected: index, color }; return; }
        this.selectedItem = index;
        // The selectedItem watcher carries the old finish over; the swatch wins after it.
        this.$nextTick(() => { this.selectedColor = color; });
      },
      onDrawerColor( index ) {
        if ( this.compare ) this.compare = { ...this.compare, color: index };
        else this.selectedColor = index;
      },
      onDescribe( item ) {
        if ( item ) this.showDescription({ ...item, step: { title: this.step.title } }, false );
      },
      startWipe( e ) {
        const box = this.$refs.bikeBox && this.$refs.bikeBox.getBoundingClientRect();
        if ( !box ) return;
        const move = ev => { this.wipe = Math.min( 100, Math.max( 0, ( ev.clientX - box.left ) / box.width * 100 )); };
        const stop = () => { window.removeEventListener( 'pointermove', move ); window.removeEventListener( 'pointerup', stop ); };
        window.addEventListener( 'pointermove', move );
        window.addEventListener( 'pointerup', stop );
        move( e );
      },
      // Rim depth marked on both rims for a few seconds after a wheel change
      // (rear = A, front = B when comparing, matching the wipe's two sides).
      showRimDepth() {
        clearTimeout( this.rimTimer );
        this.rimCallout = null;
        const a = this.stepSelection, b = this.compareEntry;
        if ( !this.rimKey ) return;
        // After the new wheel images are drawn and any resize has settled.
        this.rimTimer = setTimeout(() => {
          const bike = this.$refs.bike, box = this.$refs.bikeBox;
          if ( !bike || !box ) return;
          const br = box.getBoundingClientRect(), cr = bike.$el.getBoundingClientRect();
          const dx = cr.left - br.left, dy = cr.top - br.top;
          const marks = bike.wheelMarks().map( wheel => {
            const rear = wheel.side === 'Left';
            const mm = rimDepthMm(( rear || !b ? a : b ).item, rear ? 'rear' : 'front' );
            if ( !mm ) return null;
            // A 700c rim's outer radius is about 316 mm (622 mm bead seat + rim walls).
            const px = Math.max( 6, mm * wheel.r / 316 );
            const edge = dx + wheel.x + ( rear ? -wheel.r : wheel.r );
            const y = dy + wheel.y;
            const outside = ( rear ? edge : br.width - edge ) > 104;
            return {
              side: rear ? 'rear' : 'front', text: `${ mm } mm`, outside,
              tick: { left: `${ rear ? edge : edge - px }px`, top: `${ y }px`, width: `${ px }px` },
              label: outside
                ? { top: `${ y }px`, ...( rear ? { right: `${ br.width - edge + 28 }px` } : { left: `${ edge + 28 }px` }) }
                : { top: `${ y - 12 }px`, ...( rear ? { left: `${ Math.max( 4, edge - 6 ) }px` } : { right: `${ Math.max( 4, br.width - edge - 6 ) }px` }) }
            };
          }).filter( Boolean );
          if ( !marks.length ) return;
          this.rimCallout = { marks };
          this.rimTimer = setTimeout(() => { this.rimCallout = null; }, 4000 );
        }, 800 );
      },
      resizeBikes() {
        // After the grid has re-laid out (the drawer slides in over 200ms).
        setTimeout(() => {
          [ this.$refs.bike, this.$refs.bikeB ].forEach( bike => bike && bike.onResize());
        }, 220 );
      },
      onChangeIndex( index ) {
        if ( ! this.isDisabled( index )) this.index = index;
      },
      // A part clicked on the bike preview opens its own step.
      onSelectPart( id ) {
        const index = this.steps.findIndex( step => step.id === id );
        if ( index < 0 ) return;
        this.detailsComplete = false;
        // Clicking a wheel or tyre on the bike opens its details as well as its list.
        if ( id === 'wheels' || id === 'tyres' ) this.spotlightFor = id;
        this.onChangeIndex( index );
        // Phones: the part's options open straight away (desktop shows them docked).
        if ( this.useDrawer && !this.wide && this.index === index ) this.drawerOpen = true;
      },
      reset() {
        this.selection = [];
        this.selectedItem = null;
        this.index = 0;
      },
      prev() {
        this.index = Math.max( this.index - 1, 0 );
      },
      next() {
        this.index = Math.min( this.index + 1, this.steps.length - 1 );
      },
      showDetails() {
        // Always open the full build overview, from any step.
        this.description.show = this.form = false;
        this.detailsComplete = true;
      },
      showDescription( item, backButton ) {
        this.form = false;
        this.description.item  = item;
        this.description.image = itemImage( item );
        this.description.backButton = backButton;
        this.description.show  = true;
      },
      showSave() {
        if (this.$refs.savedBuilds) this.$refs.savedBuilds.openSave();
      },
      startNewBuild() {
        this.detailsComplete = false;
        this.compare = null;
        this.reset();
      },
      restoreSavedBuild(id) {
        this.detailsComplete = false;
        this.getComposition(id);
      },
      showForm() {
        this.description.show = false;
        this.form = true;
      },
      setRoute( id ) {
        const { history } = window;
        if ( history ) {
          if ( id ) history.replaceState( '', '', `${appBase || '/'}?id=${ encodeURIComponent(id) }`);
          else history.replaceState( '', '', appBase || '/' );
        }
      },
      getSteps( frameset ) {
        const steps = ITEMS_LIST.slice();
        if ( frameset ) {
          if ( ! frameset.cageEnabled ) steps.splice( 7, 1 );
          if ( ! frameset.saddleEnabled ) steps.splice( 6, 1 );
          if ( ! frameset.seatpostEnabled ) steps.splice( 5, 1 );
          if ( ! frameset.wheelEnabled ) steps.splice( 3, 1 );
          if ( ! frameset.groupsetEnabled ) steps.splice( 2, 1 );
          if ( ! frameset.barEnabled ) steps.splice( 1, 1 );
        }
        return steps;
      },
      decodeBuild( id ) {
        return decodeComposition( id, {
          getSteps: this.getSteps,
          seatposts: this.seatposts || [],
          tyreWidth: validTyreWidth,
          options: step => step === 'cages' ? [this.noCages, ...this.cages]
            : step === 'pedals' ? [this.noPedals, ...this.pedals] : this[step]
        });
      },
      getComposition( id ) {
        id = id || new URLSearchParams(window.location.search).get('id');
        const selection = id && this.decodeBuild( id );
        if ( ! selection ) return;
        this.selection = selection;
        this.index = selection.length;
        this.special = false;
      },
      sortBars( bars ) {
        // Brand-aware ordering: ENVE cockpits first, then Specialized/Roval —
        // unless the selected frameset is a Specialized, which flips the two.
        const frameset = this.selection.find( a => a.item && a.props.id === 'framesets' );
        const specialized = frameset && /specialized/i.test( frameset.item.name || '' );
        const rdc = frameset && isSekaRdc(frameset.item, frameset.color);
        const rank = bar => {
          const name = bar.name || '';
          if (name === bmcCockpitName(frameset && frameset.item)) return -3;
          if (name === extremaCockpitName(frameset && frameset.item, frameset && frameset.color)) return -3;
          if (rdc && name === 'SEKA Gladius') return -2;
          if (rdc && name === 'SEKA Gladius ALTA') return -1;
          if ( /enve/i.test( name )) return specialized ? 1 : 0;
          if ( /roval|specialized/i.test( name )) return specialized ? 0 : 1;
          return 2;
        };
        return extremaBarsForFrame(sekaBarsForFrame(bmcBarsForFrame(bars, frameset && frameset.item), frameset && frameset.item, frameset && frameset.color), frameset && frameset.item, frameset && frameset.color)
          .sort(( a, b ) => rank( a ) - rank( b ));
      },
      sortPedals( pedals ) {
        // Gravel frames list off-road (two-bolt) pedals first; nothing is hidden.
        const frame = this.selectedFrame;
        const gravel = frame && /gravel/i.test( frame.category || '' );
        const rank = pedal => Number( /gravel/i.test( pedal.category || '' ) !== !!gravel );
        return pedals.slice().sort(( a, b ) => rank( a ) - rank( b ));
      },
      sortWheels( wheels ) {
        const frameset = this.selection.find( a => a.props.id === 'framesets' && a.item );
        const name = frameset ? frameset.item.name || '' : '';
        const preferred = /\b(specialized|crux)\b/i.test( name ) ? /\broval\b/i
          : /\b(enve|mog)\b/i.test( name ) ? /\benve\b/i : null;
        if ( ! preferred ) return wheels;
        // Promote the frame's matching wheel brand, preserving catalogue order
        // within each group and keeping every alternative available.
        return wheels.slice().sort(( a, b ) =>
          Number( preferred.test( b.name || '' )) - Number( preferred.test( a.name || '' )));
      },
      getQuery( color ) {
        return this.composition.map( a => {
          const code = [
            a.item ? a.item.id : 0,
            color && a.item && a.item.colors[ a.color ] ? a.color : 0
          ];
          if (color && a.item.type === 'tyres' && a.tyreWidth) code.push(Math.round(a.tyreWidth * 10));
          return a.item.type.charAt(0) + code.join( SEPARATOR );
        }).join('');
      },
      redimension() {
        this.wide = window.innerWidth > 700;
        const { info, footer } = this.$refs;
        if ( info && window.innerWidth < 1024 ) this.contentMargin = info.$el.clientHeight + 32;
        else this.contentMargin = 0;
        if ( footer ) this.footerHeight = footer.$el.clientHeight + 32;
        else this.footerHeight = 0;
      },
      resizeBike( dim ) {
        this.$nextTick(() => {
          if ( this.$refs.bike ) {
            this.$refs.bike.onResize( dim );
          }
        });
      },
      // The rendered bike as PNG data, redrawn so every layer is in place.
      async captureBike() {
        const bike = this.$refs.bike;
        await bike.mountBike();
        await this.$nextTick();
        bike.draw();
        if (!bike.buffer.length || bike.failedGroupset) throw new Error('Bike images are not ready');
        return bike.$refs.canvas.toDataURL('image/png');
      },
      async fetchLogo() {
        const response = await fetch(`${appBase}/velodrom-logo.png`);
        if (!response.ok) throw new Error('Logo unavailable');
        return new Uint8Array(await response.arrayBuffer());
      },
      buildLink() {
        return new URL(`${appBase || '/'}?id=${encodeURIComponent(this.compositionID)}`, window.location.origin).href;
      },
      // Renders the story card once, then shares it. Browsers that need a fresh
      // tap for the share sheet keep the image, so the next tap shares at once.
      async shareStory() {
        if (this.storyBusy) return;
        this.storyBusy = true;
        this.storyError = '';
        try {
          const { renderStoryImage, storyBlob, shareStoryImage } = await import('@/utils/storyImage');
          if (!this.storyFile) {
            const find = id => this.composition.find(a => a.props && a.props.id === id);
            const frameset = find('framesets');
            const parts = [['Groupset', 'groupsets'], ['Wheels', 'wheels']]
              .map(([label, id]) => ({ label, value: find(id) && find(id).item.name }));
            const [bikeImage, logo] = await Promise.all([this.captureBike(), this.fetchLogo()]);
            const canvas = await renderStoryImage({
              bikeImage, logo, parts,
              title: frameset ? frameset.item.name : 'Your bike',
              price: this.price,
              offer: this.specialBuild && this.specialBuild.price,
              // A partial sum would understate the bike on a card with no room for caveats.
              weight: this.weight && !this.weight.missing.length ? `≈${kg(this.weight.grams)} kg` : null
            });
            const name = (frameset && frameset.item.name || 'my-bike').replace(/\([^)]*\)/g, '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
            this.storyFile = { blob: await storyBlob(canvas), filename: `velodrom-${name}.png` };
          }
          await shareStoryImage({
            ...this.storyFile,
            title: 'My Velodrom build',
            text: 'My bike, built on the Velodrom bike builder:',
            url: this.buildLink()
          });
        } catch (error) {
          if (!(error && error.name === 'NotAllowedError')) {
            console.error('Story image failed', error);
            this.storyError = 'We could not create the image. Please let the bike images finish loading and try again.';
          }
        } finally {
          this.storyBusy = false;
        }
      },
      async downloadBuildPdf() {
        if (this.pdfBusy) return;
        this.pdfBusy = true;
        this.pdfError = '';
        try {
          const items = this.detailsInfo.map(item => ({ ...item }));
          const buildUrl = this.buildLink();
          const { createBuildPdf } = await import('@/utils/buildPdf');
          const bikeImage = await this.captureBike();
          const logo = await this.fetchLogo();
          const doc = createBuildPdf({ items, bikeImage, logo, buildUrl, offer: this.specialBuild && this.specialBuild.price });
          const name = (items[0] && items[0].name || 'my-bike').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
          doc.save(`velodrom-${name}.pdf`);
        } catch (error) {
          console.error('PDF export failed', error);
          this.pdfError = 'We could not create your PDF. Please let the bike images finish loading and try again.';
        } finally {
          this.pdfBusy = false;
        }
      },
      initPrint() {
        this.print = true;
        this.$nextTick(() => {
          window.print();
        });
      },
      onPrint() {
        this.print = true;
        this.$refs.bike.onResize( this.detailsComplete ? {
          containerWidth: 400,
          containerHeight: 240
        } : {} );
      },
      onAfterPrint() {
        this.print = false;
      }
    }
  }
</script>

<style>
.allied-preview-note { position: relative; z-index: 2; margin: 0 24px; padding: 10px 14px; background: #f4f0e7; color: #514633; font-size: 13px; }

  .builder-body, .builder-breadcrumbs, .builder-content, .builder-footer, .builder-nav,
  .builder-nav--top, .builder-nav--steps, .builder-nav--items, .builder-nav--bikefit, .builder-nav--continue,
  .builder-info {
    position: absolute;
  }
  .builder-nav--top, .builder-nav--steps, .builder-nav--items, .builder-nav--bikefit, .builder-info,
  .builder-breadcrumbs, .builder-content, .builder-footer {
    width: 100%;
  }
  .builder-breadcrumbs, .builder-nav--top, .builder-info {
    top: 0;
    left: 0;
  }
  .builder-body {
    display: flex;
    flex-direction: column;
    bottom: 0;
    left: 0;
    right: 400px;
  }
  .builder-content {
    flex: 1 1 auto;
    top: 30px;
    bottom: 75px;
    left: 0;
  }
  .builder-footer {
    bottom: 0;
    left: 0;
  }
  .builder-nav {
    width: 400px;
    right: 0;
    bottom: 0;
    background-color: white;
    border-left: 1px solid var(--v-light-base);
  }

  .builder-content .btn-special-build {
    position: absolute;
    top: 20px;
    right: 20px;
  }

  /* NAV */

  .builder-nav--top {
    display: flex;
    height: 48px;
    justify-content: space-between;
    align-items: center;
  }
  .builder-nav--steps {
    text-align: center;
    height: 30px;
    top: 48px;
    left: 0;
  }
  .builder-nav--items, .builder-nav--bikefit {
    top: 78px;
    bottom: 70px;
    left: 0;
  }
  .builder-nav--continue {
    bottom: 0;
    left: 0;
  }
  .builder-nav--bikefit {
    overflow: auto;
  }

  /* CONTENT */

  .builder-start {
    background: url('../../../assets/frameset-bg.svg') no-repeat center;
    background-size: 320px;
  }
  .builder-start p {
    max-width: 440px;
    color: var(--bb-secondary) !important;
  }
  .builder-start h1, .builder-start h2, .builder-start h3 {
    font-family: "Open sans", Helvetica, Arial, sans-serif;
    letter-spacing: normal;
    color: var(--bb-primary) !important;
    margin-bottom: 12px !important;
  }
  .builder-start h1 {
    font-size: 1.875rem;
    font-weight: 400;
    line-height: 1;
  }
  .builder-start h2 {
    font-size: 1.875rem;
    font-weight: 300;
    line-height: 1;
  }
  .builder-start h3 {
    font-size: 1.25rem;
    font-weight: 400;
    line-height: 1.2;
  }
  .builder-start--info {
    max-width: none !important;
    position: absolute;
    text-align: center;
    left: 0; right: 0;
    top: 50%;
  }

  .builder-bike {
    padding-top: 50px;
    width: 100%;
    height: 100%;
    text-align: center;
    position: relative;
  }
  .builder-bike > div {
    height: 100%;
  }
  .builder-bike > .cage-edit-panel {
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    height: auto;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: rgba(233, 30, 99, .08);
    border: 1px dashed #e91e63;
    border-radius: 4px;
  }
  /* Top left of the preview, clear of its view/Ride tools (top right; a full
     row of their own on phones) and of the ride speedometer (z-index 4). */
  .builder-bike > .build-presets {
    position: absolute;
    top: 32px;
    left: 32px;
    right: 360px;
    z-index: 3;
  }
  @media ( max-width: 700px ) {
    .builder-bike > .build-presets { top: 12px; left: 12px; right: 300px; }
  }
  @media ( max-width: 600px ) {
    .builder-bike > .build-presets { top: 42px; left: 6px; right: 6px; }
  }
  .cage-edit-panel--label {
    font-size: 12px;
    font-weight: 600;
    color: #e91e63;
    letter-spacing: .5px;
  }

  /* DETAILS COMPLETE */

  .show-details-complete .builder-header {
    position: fixed;
    width: 100%;
    z-index: 6;
  }
  .show-details-complete .builder-body {
    display: block;
    padding: 0 3.175%;
    max-width: 1200px;
    margin: 0 auto;
    right: 0;
  }
  .show-details-complete .builder-content {
    right: 20px;
    left: auto;
    bottom: auto;
    max-width: 720px;
  }
  .show-details-complete .builder-bike {
    height: auto;
    position: relative;
    padding-top: 0;
    margin-top: 20px;
  }
  .show-details-complete .builder-bike::before {
    display: block;
    content: '';
    padding-top: 71.428%;
  }
  .show-details-complete .builder-bike > div {
    position: absolute;
    width: 100%;
    top: 0; left: 0;
  }

  /* MEDIA */

  @media ( max-width: 1120px ) {
    .builder-content {
      bottom: 154px;
    }
  }

  @media ( max-width: 1024px ) {
    .builder-body {
      right: 0;
      bottom: 300px;
      overflow-y: auto;
      overflow-x: hidden;
    }
    .builder-breadcrumbs, .builder-nav--continue {
      display: none;
    }
    .builder-content {
      position: static;
    }
    .builder-start {
      position: relative;
    }
    .builder:not(.builder-initial) .builder-content {
      height: 100%;
    }
    .builder-footer, .builder-info, .builder-nav--bikefit {
      position: static;
    }
    .builder-nav {
      width: 100%;
      top: auto;
      height: 298px;
      border-top: 1px solid var(--v-light-base);
      border-left: 0;
    }
    .builder-nav--top {
      top: auto;
      bottom: 0;
    }
    .builder-nav--steps {
      display: none;
    }
    .builder-nav--items {
      top: 0;
      bottom: 48px;
    }
    .builder-nav--items > header {
      justify-content: flex-end;
    }
    .builder-nav--items > header > input {
      max-width: 320px;
    }
    .builder-bike {
      padding-top: 0;
    }
    .builder-nav--bikefit {
      position: static;
      overflow: visible;
    }

    /* On Bike fit */

    .builder.show-bike-fit {
      padding-top: 70px;
      overflow: auto;
    }
    .show-bike-fit .builder-header {
      position: fixed;
      width: 100%;
      z-index: 6;
    }
    .show-bike-fit .builder-body {
      position: relative;
      overflow: visible;
      height: 100%;
      top: 0;
    }
    .show-bike-fit .builder-content {
      position: absolute;
      height: auto;
      bottom: 224px;
      top: 0;
    }
    .show-bike-fit .builder-footer {
      position: absolute;
      bottom: 50px;
    }
    .show-bike-fit .builder-nav {
      margin-top: -50px;
      position: static;
      height: auto;
    }
    .show-bike-fit .builder-nav--top {
      position: static;
    }
    .show-bike-fit .builder-nav--continue {
      position: static;
      display: block;
    }
  }

  /* Step counter in the black bar — mobile only */
  .builder-nav--count {
    display: none;
    font-weight: 400;
    font-size: .85rem;
    opacity: .8;
  }

  /* MOBILE (phones) — bespoke-style arrangement */
  @media ( max-width: 600px ) {

    .builder-nav--count {
      display: inline;
    }

    /* Start screen: ghost frame + slogan on top, intro copy at the bottom */
    .builder-start {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: 24px !important;
      background-position: center 14vh;
      background-size: min(84vw, 400px);
    }
    .builder-start--info {
      top: 20vh;
      padding: 0 40px;
      font-weight: 700;
      letter-spacing: .04em;
    }

    /* Slimmer bottom panel */
    .builder-body {
      bottom: 266px;
    }
    .builder-nav {
      height: 264px;
    }
  }

  @media( max-width: 992px ) {
    .show-details-complete .builder-content {
      max-width: 100%;
    }
    .show-details-complete .builder-bike {
      margin-top: 70px;
    }
    .show-details-complete .builder-bike::before {
      padding-top: 56%;
    }
    .builder.show-bike-fit {
      padding-top: 60px;
    }
    .builder-content .btn-special-build {
      top: 0;
    }
  }

  @media ( max-width: 480px ) {
    .builder-body {
      bottom: 250px;
    }
    .builder-nav {
      height: 250px;
    }
  }

  @media ( max-width: 520px ) {
    .builder-start {
      background-position: center bottom;
      background-size: 90%;
    }
    .builder-start > div {
      border-bottom: 1px solid;
    }
    .builder-start--info {
      font-size: 1rem !important;
      position: static;
      text-align: left;
      padding-top: 1rem;
    }
  }

  @media print {
    .details-divider {
      margin-bottom: 1000px;
    }
    .builder.layer, .builder-body, .v-application {
      position: relative !important;
      top: 0 !important;
    }
    .show-details-complete .builder-content {
      max-width: 360px !important;
    }
    .show-details-complete .builder-bike {
      margin-top: 20px !important;
    }
    .show-details-complete .builder-bike::before {
      padding-top: 71.428% !important;
    }
    .details-complete {
      text-align: left;
      padding-top: 32px;
    }
    .details-complete > .details-wrapper {
      padding-right: 360px;
    }
    .details-complete .builder-bike {
      display: none;
    }
    .builder-header,
    .details-complete .btn-return,
    .details-complete .btn-form,
    .details-complete .detail-actions {
      display: none;
    }
    .details-complete .col-12 {
      flex: 0 0 33.3333333333% !important;
      max-width: 33.3333333333% !important;
    }
    .details-complete .detail-item-info .caption {
      font-size: 8px !important;
    }
    .details-complete .details-wrapper > p {
      font-size: 12px !important;
    }
    .details-complete .detail-item-info .headline,
    .details-complete .detail-price,
    .details-complete h2 {
      font-size: 12px !important;
      text-transform: none !important;
    }
    .detail-image-container {
      height: 100px !important;
    }
  }
</style>

<style lang="scss" src="./modern.scss"></style>
